import type { SizesAndQuantityItem } from "../types";
import { getRandomId } from "../utils";

type FallbackValues = {
  width: number | string;
  height: number | string;
  quantity: number;
};

type BuildSeedOptions = {
  fallbackValues: FallbackValues;
  id: string;
};

/**
 * Builds the post-enhancement seed written to `artwork.sizesAndQuantities`
 * by the WS handler in `dtf-channel.ts` once the upscaler finishes and the
 * customer has no customer-edited rows yet.
 *
 * Emits a single Custom-Size row using the caller's fallback dimensions.
 * `isAutoSeed: true` keeps the BAT-1913 bounds-validator suppression active
 * until the customer edits — first edit flips `isSizesEdited`, downstream
 * `mapSizeAndQuantity` strips `isAutoSeed` before the cart payload reaches
 * the backend.
 */
export function buildDefaultPlacementSeed({
  fallbackValues,
  id
}: BuildSeedOptions): SizesAndQuantityItem[] {
  return [
    {
      preset: "custom",
      width: Number(fallbackValues.width),
      height: fallbackValues.height,
      quantity: fallbackValues.quantity,
      isAutoSeed: true,
      id
    }
  ];
}

// `Number(undefined)` is NaN; `Number(null)` is 0 — both unwanted. Coerce
// because BigDecimal.to_json on Ruby serializers emits strings (`"8.5"`).
export function asFiniteNumber(raw: unknown): number | undefined {
  if (raw === null || raw === undefined) return undefined;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

type DimensionBounds = {
  minWidth?: number | null | undefined;
  maxWidth?: number | null | undefined;
  minHeight?: number | null | undefined;
  maxHeight?: number | null | undefined;
};

type FallbackDimensions = {
  width: number | string;
  height: number | string;
};

/**
 * Resolves the inches written into the post-upload Custom seed from the
 * `with_bg_metadata` carried on the WS payload.
 *
 * `with_bg_metadata.width_inch` / `height_inch` are produced by
 * `Dtf::ArtworkProcessing::CalculateMetadata` against the bg-removed,
 * trimmed image at the canonical 300 PPI (`Constants::PIXELS_IN_INCH`).
 * They reflect the customer's actual upload size, not the upscaler ceiling.
 *
 * Falls back to the caller's defaults when the WS payload is missing the
 * keys (e.g. SVG uploads where `extract_bg_metadata` is skipped). Clamps
 * any result to the product's min/max bounds so the auto-seed cannot
 * persist out-of-range dimensions past the customer's first edit
 * (`isAutoSeed` flips off, the bounds validators activate).
 */
export function resolveCustomSeedDimensions(opts: {
  bgRemovedMetadata: { width_inch?: unknown; height_inch?: unknown } | null | undefined;
  fallback: FallbackDimensions;
  bounds: DimensionBounds;
}): { width: number; height: number } {
  const bgWidth = asFiniteNumber(opts.bgRemovedMetadata?.width_inch);
  const bgHeight = asFiniteNumber(opts.bgRemovedMetadata?.height_inch);

  const rawWidth = bgWidth ?? asFiniteNumber(opts.fallback.width) ?? 1;
  const rawHeight = bgHeight ?? asFiniteNumber(opts.fallback.height) ?? 1;

  return {
    width: clampInches(rawWidth, opts.bounds.minWidth, opts.bounds.maxWidth),
    height: clampInches(rawHeight, opts.bounds.minHeight, opts.bounds.maxHeight)
  };
}

function clampInches(
  value: number,
  min: number | null | undefined,
  max: number | null | undefined
) {
  let clamped = value;
  if (typeof min === "number" && Number.isFinite(min)) clamped = Math.max(clamped, min);
  if (typeof max === "number" && Number.isFinite(max)) clamped = Math.min(clamped, max);
  return clamped;
}

// Mirrors the row shape used by `seedAutoCustomSize` in dtf-channel.ts (DTF-001
// WS path); keep both in lockstep or fold them together when signatures
// converge.
export function buildProColorUploadSeed(opts: {
  artworkId: number | string;
  rawMetadata: Record<string, unknown> | null | undefined;
  fallbackDimensions: { width: number | string; height: number | string };
  defaultQuantity: number | undefined;
}): Record<string, unknown> {
  const md = (opts.rawMetadata || {}) as Record<string, unknown>;
  const w =
    asFiniteNumber(md["width_inch"]) ??
    asFiniteNumber(md["widthInch"]) ??
    asFiniteNumber(opts.fallbackDimensions.width) ??
    1;
  const h =
    asFiniteNumber(md["height_inch"]) ??
    asFiniteNumber(md["heightInch"]) ??
    asFiniteNumber(opts.fallbackDimensions.height) ??
    1;
  return {
    id: opts.artworkId,
    width: w,
    height: h,
    isLoading: false,
    isFile: false,
    sizesAndQuantities: [
      {
        preset: "custom",
        width: w,
        height: h,
        quantity: opts.defaultQuantity || 1,
        isAutoSeed: true,
        id: getRandomId()
      }
    ]
  };
}

/**
 * Decides what `sizesAndQuantities` should be after the upscaler completes.
 * Pure function of `(existing, seedFn)` — extracted so the idempotency
 * contract (repeated WS events must not re-seed) is testable without a
 * Pinia store.
 *
 * Empty existing → seed (first post-enhancement WS event). Anything else
 * (customer edits OR a prior auto-seed row) is preserved untouched —
 * WS-driven `updateArtworkValuesById` does not flip `isSizesEdited`, so
 * without this guard every subsequent WS tick (fetch_delivery_date,
 * downstream markEnhancementCompleted) would re-seed with fresh
 * `getRandomId()`s, unmounting React rows mid-flow while the customer is
 * picking a size.
 */
export function resolveSeedAfterEnhancement(
  existing: Record<string, unknown>[],
  seedFn: () => Record<string, unknown>[]
): Record<string, unknown>[] {
  return existing.length === 0 ? seedFn() : existing;
}
